import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addDriver,
  bulkInsertDriver,
  deleteDriver,
  updateDriver,
} from "redux/actions/driver";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import DriverRes from "types/res/driver/DriverRes";
import { FormType } from "components/FormField";
import CreateDriverReq from "types/req/driver/CreateDriverReq";
import UpdateDriverReq from "types/req/driver/UpdateDriverReq";
import CsvImport from "components/CsvImport";
import { Box, Button, TextField } from "@material-ui/core";
import { CsvFieldType } from "utils/CsvUtils";
import EnvironmentUtils from "utils/EnviromentUtils";
import DriverApi from "api/DriverApi";

const headers: { key: keyof DriverRes; label: string }[] = [
  { key: "name", label: "名前" },
  { key: "email", label: "メールアドレス" },
  { key: "address", label: "住所" },
  { key: "tel", label: "電話番号" },
  { key: "carType", label: "車種" },
  { key: "carColor", label: "車色" },
  { key: "carNumber", label: "車ナンバー" },
  { key: "remarks", label: "備考" },
  { key: "sort", label: "並び順" },
  { key: "lineUserId" as any, label: "LINE連携" },
  { key: "notifyMethod" as any, label: "通知方式" },
];

const Driver: React.FC = () => {
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const [openCsvImport, setOpenCsvImport] = useState(false);

  const [driver, setDriver] = useState<DriverRes[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const loadDrivers = useCallback(
    async (opts: { limit: number; offset: number; name: string }) => {
      if (!companyId) return;
      setIsLoading(true);
      try {
        const [list, cnt] = await Promise.all([
          DriverApi.findAllWithPagination(companyId, {
            limit: opts.limit,
            offset: opts.offset,
            name: opts.name || undefined,
          }),
          DriverApi.count(companyId, { name: opts.name || undefined }),
        ]);
        setDriver(list);
        setTotalCount(cnt);
      } finally {
        setIsLoading(false);
      }
    },
    [companyId]
  );

  useEffect(() => {
    loadDrivers({ limit, offset: offset * limit, name: searchName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, limit, offset]);

  const onSearch = () => {
    setOffset(0);
    loadDrivers({ limit, offset: 0, name: searchName });
  };

  const forms = [
    {
      label: "名前",
      key: "name",
      type: FormType.Text,
    },
    {
      label: "メールアドレス",
      key: "email",
      type: FormType.Text,
    },
    {
      label: "初期パスワード",
      key: "password",
      type: FormType.Text,
    },
    {
      label: "住所",
      key: "address",
      type: FormType.TextArea,
    },
    {
      label: "電話番号",
      key: "tel",
      type: FormType.Text,
    },
    {
      label: "車種",
      key: "carType",
      type: FormType.Text,
    },
    {
      label: "車色",
      key: "carColor",
      type: FormType.Text,
    },
    {
      label: "車ナンバー",
      key: "carNumber",
      type: FormType.Text,
    },
    {
      label: "備考",
      key: "remarks",
      type: FormType.TextArea,
    },
    {
      label: "並び順",
      key: "sort",
      type: FormType.Number,
    },
    // LINE 連携はドライバー本人がモバイル画面から行います。
    // ここで lineUserId を手入力するフォームは廃止しました。
  ];

  const csvHeader = [
    {
      label: "名前",
      key: "name",
      type: CsvFieldType.Text,
    },
    {
      label: "メールアドレス",
      key: "email",
      type: CsvFieldType.Text,
    },
    {
      label: "初期パスワード",
      key: "password",
      type: CsvFieldType.Text,
    },
    {
      label: "住所",
      key: "address",
      type: CsvFieldType.Text,
    },
    {
      label: "電話番号",
      key: "tel",
      type: CsvFieldType.Text,
    },
    {
      label: "車種",
      key: "carType",
      type: CsvFieldType.Text,
    },
    {
      label: "車色",
      key: "carColor",
      type: CsvFieldType.Text,
    },
    {
      label: "車ナンバー",
      key: "carNumber",
      type: CsvFieldType.Text,
    },
    {
      label: "備考",
      key: "remarks",
      type: CsvFieldType.Text,
    },
    {
      label: "並び順",
      key: "sort",
      type: CsvFieldType.Number,
    },
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop={1}
        marginX={2}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <TextField
            label="ドライバー名検索"
            size="small"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSearch();
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={onSearch}
            disabled={isLoading}
          >
            検索
          </Button>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.open(EnvironmentUtils.getDriverDomain())}
          >
            ドライバーモバイルへ
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCsvImport((prev) => !prev)}
          >
            csvインポート
          </Button>
        </Box>
      </Box>
      {openCsvImport && (
        <CsvImport
          templatePath={"/csv/driver.csv"}
          templateName={"driver_template.csv"}
          csvHeader={csvHeader}
          addType={CreateDriverReq}
          addFunc={(formData) => bulkInsertDriver(companyId, formData)}
        />
      )}
      <DriverTable
        title={"ドライバー"}
        formId="driverForm"
        forms={forms}
        rows={headers}
        data={driver}
        isLoading={isLoading}
        totalCount={totalCount}
        limit={limit}
        offset={offset}
        setLimit={setLimit}
        setOffset={(v: any) => setOffset(Number(v))}
        addFunc={(formData: any) => async (d: any) => {
          const r = await addDriver(companyId, formData)(d);
          await loadDrivers({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        updateFunc={(formData: any) => async (d: any) => {
          const r = await updateDriver(companyId, formData)(d);
          await loadDrivers({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        deleteFunc={(item: any) => async (d: any) => {
          const r = await deleteDriver(companyId, {
            driverId: item.driverId,
          })(d);
          await loadDrivers({
            limit,
            offset: offset * limit,
            name: searchName,
          });
          return r;
        }}
        addType={CreateDriverReq}
        updateType={UpdateDriverReq}
        values={[
          (s) => s.name,
          (s) => s.email,
          (s) => s.address,
          (s) => s.tel,
          (s) => s.carType,
          (s) => s.carColor,
          (s) => s.carNumber,
          (s) => s.remarks,
          (s) => s.sort || "未設定",
          (s: any) => s.lineUserId ? "✅ 連携済" : "❌ 未連携",
          (s: any) => {
            const methods: any = { email: "メールのみ", line: "LINEのみ", both: "メール+LINE" };
            return methods[s.notifyMethod] || "メールのみ";
          },
        ]}
      />
    </>
  );
};

const DriverTable = styled<
  React.FC<CommonTableProps<DriverRes, CreateDriverReq, UpdateDriverReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default Driver;
